import { useState, useEffect } from "react";
import axios from "axios";
import { Bar, Pie } from "react-chartjs-2";
import { useGlobalContext } from "../../GlobalContext";
import {
  Truck,
  PieChart as PieIcon,
  BadgeCheck,
  XCircle,
  Loader,
  AlertCircle,
  Send,
  Download,
} from "lucide-react";
import Logo from "../../Images/Packky.svg";
import LoadingScreen from "../Utils/LoadingScreen";
import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";

import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

const ReportsDashboard = () => {
  const { accessToken, baseurl, setIsUnauthorised,AdminId } = useGlobalContext();
  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [loading, setLoading] = useState(false);

  const [restaurantData, setRestaurantData] = useState([]);
  const [driverTaskAnalytics, setDriverTaskAnalytics] = useState([]);
  const [taskStatusAnalytics, setTaskStatusAnalytics] = useState([]);

  const getStatusConfig = (status) => {
    const statusLower = status.toLowerCase();
    if (
      ["completed", "successful", "delivered", "arrived"].includes(statusLower)
    ) {
      return { Icon: BadgeCheck, color: "#059669" }; 
    }
    if (
      ["failed", "cancelled", "canceled", "error", "rejected"].includes(
        statusLower
      )
    ) {
      return { Icon: XCircle, color: "#dc2626" }; 
    }
    if (["food is ready"].includes(statusLower)) {
      return { Icon: Send, color: "#14b8a6" };
    }
    if (["declined", "unassigned"].includes(statusLower)) {
      return { Icon: XCircle, color: "#a855f7" }; 
    }
    if (["pending", "inprogress", "assigned"].includes(statusLower)) {
      return { Icon: Loader, color: "#2563eb" }; 
    }
    if (["sent", "dispatched"].includes(statusLower)) {
      return { Icon: Send, color: "#f59e0b" }; 
    }
    return { Icon: AlertCircle, color: "#6b7280" };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const payload = { fromDate, toDate ,adminId:AdminId};
      const config = {
        headers: { Authorization: `Bearer ${accessToken}` },
      };

      const [res1, res2, res3] = await Promise.all([
        axios.post(
          `${baseurl}/api/Task/v1.0/getTaskAnalytics`,
          payload,
          config
        ),
        axios.post(
          `${baseurl}/api/Driver/v1.0/getDriverAnalytics`,
          payload,
          config
        ),
        axios.post(
          `${baseurl}/api/Task/v1.0/getTaskAnalyticsByStatus`,
          payload,
          config
        ),
      ]);

      setRestaurantData(res1.data.data || []);
      setDriverTaskAnalytics(res2.data.data || []);
      setTaskStatusAnalytics(res3.data.data || []);
    } catch (err) {
      console.error("Error loading reports:", err);
      if (err?.response?.status === 401) setIsUnauthorised(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const driverBarChartData = {
    labels: driverTaskAnalytics.map((d) => d.driverName),
    datasets: [
      {
        label: "Tasks",
        data: driverTaskAnalytics.map((d) => d.taskCount),
        backgroundColor: driverTaskAnalytics.map(
          (_, i) =>
            ["#2563eb", "#f59e0b", "#059669", "#dc2626", "#8b5cf6", "#22c55e"][
              i % 5
            ]
        ),
        borderRadius: 6,
      },
    ],
  };

  const restaurantPieChartData = {
    labels: restaurantData.map((r) => r.shopName),
    datasets: [
      {
        data: restaurantData.map((r) => r.taskCount),
        backgroundColor: [
          "#2563eb",
          "#f59e0b",
          "#059669",
          "#22c55e",
          "#dc2626",
          "#8b5cf6",
        ],
      },
    ],
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <img src={Logo} alt="Logo" className="h-10" />
          <p className="text-2xl font-bold ps-3">Report</p>
        </div>

        <button
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded shadow transition-colors"
          onClick={() => {
            exportAnyJsonToExcel({
              data: driverTaskAnalytics,
              filePrefix: "DriverReportExport",
              sheetName: "DriverReport",
            });
            exportAnyJsonToExcel({
              data: restaurantData,
              filePrefix: "RestaurentReportExport",
              sheetName: "RestaurentReport",
            });
          }}
        >
          <Download className="w-4 h-4" />
          Download Report
        </button>
      </div>

      {loading ? (
        <LoadingScreen />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {taskStatusAnalytics.map((status) => {
              const { Icon, color } = getStatusConfig(status.taskStatus);
              return (
                <div
                  key={status.id}
                  className="group relative bg-white p-3 shadow rounded text-center border-l-4 transition-all duration-300 hover:scale-[1.02]"
                  style={{ borderColor: color }}
                >
                  <div
                    className="absolute inset-0 rounded z-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ backgroundColor: color }}
                  ></div>

                  <div className="relative z-10">
                    <div className="flex items-center justify-center mb-2">
                      <Icon className="w-6 h-6 mr-2 text-gray-600 transition-colors duration-300 group-hover:text-white" />
                      <p className="text-lg capitalize font-medium text-gray-600 transition-colors duration-300 group-hover:text-white">
                        {status.taskStatus}
                      </p>
                    </div>
                    <p className="text-xl font-bold text-gray-800 transition-colors duration-300 group-hover:text-white">
                      {status.taskCount}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
          <hr />

          {/* Filters + Pie Chart */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 ">
            <div className="space-y-3 border rounded bg-white p-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  From Date
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="p-2 border rounded w-full text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  To Date
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="p-2 border rounded w-full text-sm"
                />
              </div>
              <button
                onClick={fetchData}
                className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded w-full text-sm"
              >
                Apply Filter
              </button>
            </div>
            <div className="col-span-2 bg-white p-4 shadow rounded">
              <h2 className="text-xl mb-2 flex items-center gap-2">
                <PieIcon className="w-4 h-4 text-blue-600" /> Restaurant Orders
                Distribution
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 overflow-y-auto max-h-64 pr-2">
                  {restaurantData.map((r, i) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between border rounded px-3 py-2 text-sm"
                      style={{
                        borderLeft: `4px solid ${
                          [
                            "#2563eb",
                            "#f59e0b",
                            "#059669",
                            "#dc2626",
                            "#8b5cf6",
                          ][i % 5]
                        }`,
                      }}
                    >
                      <span className="font-medium">{r.shopName}</span>
                      <span className="font-semibold text-gray-700">
                        {r.taskCount}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="h-64 border-l">
                  <Pie
                    data={restaurantPieChartData}
                    options={{ maintainAspectRatio: false }}
                  />
                </div>
              </div>
            </div>
          </div>
          <hr />

          {/* Driver Bar Graph */}
          <div className="bg-white p-4 shadow rounded">
            <h2 className="text-xl mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5 text-green-600" /> Driver-wise Task
              Count
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="h-64 border-r pr-3">
                <Bar
                  data={driverBarChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: {
                      duration: 800,
                      easing: "easeOutBounce",
                    },
                    plugins: { legend: { display: false } },
                  }}
                />
              </div>
              <div className="overflow-y-auto max-h-64 pr-2 space-y-2">
                <p className="text-xl">Driver</p>
                {driverTaskAnalytics.map((d, i) => (
                  <div
                    key={d.id}
                    className="flex items-center justify-between border rounded px-3 py-2 text-sm"
                    style={{
                      borderLeft: `4px solid ${
                        ["#2563eb", "#f59e0b", "#059669", "#dc2626", "#8b5cf6"][
                          i % 5
                        ]
                      }`,
                    }}
                  >
                    <span className="font-medium">{d.driverName}</span>
                    <span className="font-semibold text-gray-700">
                      {d.taskCount}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportsDashboard;
